// - fx/effects/interpolateCSSVars.js
import { readCSSVars, applyCSSVars } from "../../utils/cssVars.js";

/**
 * interpolateCSSVars — Generic CSS Variable Interpolator
 * ------------------------------------------------------
 * This utility creates a ready-to-use FX callback that interpolates
 * one or more CSS variables based on scroll progress t ∈ [0, 1].
 *
 * It allows designers to define:
 *   --start-x, --end-x, --start-opacity, --end-opacity, etc.
 *
 * …and the JS engine will smoothly interpolate:
 *   --cx, --cy, --scale, --posY, --blur, etc.
 *
 * WHY USE THIS?
 * -------------
 * - Keeps styling fully in CSS (media queries, theme overrides, breakpoints)
 * - Lets developers drive only the "timeline" via JS
 * - Works for complex properties: clip-path, gradients, masks, transforms
 * - Supports multiple variables in one call using a simple mapping table
 * - Reusable for any kind of morphing / interpolation effect
 *
 * OPTIONS
 * -------
 * interpolateCSSVars({
 *   map: {
 *     "--target-var": ["--start-var", "--end-var"],
 *     ...
 *   },
 *   reverse: false,   // if true -> progress = 1 - progress
 *   unit: "%"         // optional ("" for transforms, "px", etc.)
 * })
 *
 * MAP FORMAT
 * ----------
 * Each entry defines:
 *   "CSS var to output": ["start var", "end var"]
 *
 * Example:
 *   map: {
 *     "--cx":   ["--start-x", "--end-x"],
 *     "--cy":   ["--start-y", "--end-y"],
 *     "--posY": ["--start-pos-y", "--end-pos-y"]
 *   }
 *
 * → At each frame:
 *     start = value from --start-x, --start-y, ...
 *     end   = value from --end-x,   --end-y, ...
 *     output = lerp(start, end, t)
 *
 * USAGE WITH FXEngine
 * --------------------
 * new FX({
 *   selector: "header .clipFx",
 *   limits: {...},
 *   callback: interpolateCSSVars({
 *     reverse: true, // optional
 *     map: {
 *       "--cx":   ["--start-x", "--end-x"],
 *       "--cy":   ["--start-y", "--end-y"],
 *       "--posY": ["--start-pos-y", "--end-pos-y"]
 *     }
 *   })
 * });
 *
 * HOW IT WORKS INTERNALLY
 * ------------------------
 * - Reads all start/end variables using readCSSVars()
 * - Computes interpolation factor u (reverse or not)
 * - Interpolates numerically: s + (e - s) * u
 * - Applies final values via applyCSSVars()
 *
 * NOTES
 * -----
 * - All CSS vars must contain numeric values (with units stripped).
 * - Unit parameter applies uniformly to all resulting vars.
 * - Perfect for clip-path ellipse transitions, gradient morphing,
 *   border-radius morphing, opacity curves, etc.
 */
export function interpolateCSSVars({ map, reverse = false, unit = "%" }) {

    return (el, progress, rect) => {

        const startVars = Object.values(map).map(pair => pair[0]);
        const endVars   = Object.values(map).map(pair => pair[1]);

        const startValues = readCSSVars(el, startVars);
        const endValues   = readCSSVars(el, endVars);

        const u = reverse ? (1 - progress) : progress;

        const output = {};

        for (const target in map) {

            const [startKey, endKey] = map[target];

            const s = startValues[startKey];
            const e = endValues[endKey];

            const value = s + (e - s) * u;

            output[target] = `${value}${unit}`;
            
        }

        applyCSSVars(el, output);

    };

}